import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Col, Container, Input, Row, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumbs";
import ErrorAlert from "../../components/Common/Error/ErrorAlert";
import SuccessAlert from "../../components/Common/Success/SuccessAlert";
import { deleteClient, fetchClients } from "../../store/client/actions";

export default function ClientList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clients = [], message } = useSelector((state) => state.clientReducer);
  const [searchTerm, setSearchTerm] = useState("");
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [clientsPerPage] = useState(5); // Number of clients per page

  useEffect(() => {
    dispatch(fetchClients()); // Fetch clients on component mount
  }, [dispatch]);

  const showToast = (msg) => {
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(fetchClients(query)); // Fetch clients with the query
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // Local filter function
  const filteredClients = clients.filter(
    (client) =>
      client?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      client?.description?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Paginate filtered clients
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(
    indexOfFirstClient,
    indexOfLastClient
  );

  const handleDelete = (clientId) => {
    dispatch(deleteClient(clientId)).then((response) => {
      if (response?.message) {
        showToast(response?.message);
      }
    });
    dispatch(fetchClients()); // Re-fetch clients after deletion
  };

  const handleEdit = (client) => {
    // Pass client data when navigating to the edit page
    navigate(`/register-client`, { state: { clientData: client } });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  return (
    <div className="page-content">
      <Container fluid>
        {visible &&
          (message?.toLowerCase().includes("fail") ? (
            <ErrorAlert error={message} />
          ) : (
            <SuccessAlert message={message} />
          ))}
        <Breadcrumbs title="Manage Clients" breadcrumbItem="Client List" />
        <Row className="mb-3">
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by name or description"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col md={8} className="d-flex justify-content-end">
            <Button
              color="primary"
              onClick={() => navigate("/register-client")}
            >
              Create Client
            </Button>
          </Col>
        </Row>
        <Table striped responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentClients.length > 0 ? (
              currentClients.map((client) => (
                <tr key={client.clientId}>
                  <td>{client.name}</td>
                  <td>{client.description || ""}</td>
                  <td>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => handleEdit(client)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleDelete(client.clientId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center">
                  No clients found
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Pagination Controls */}
        <div className="d-flex justify-content-center align-items-center mt-3">
          <Button
            color="secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="mx-3">{`${currentPage} of ${totalPages}`}</span>
          <Button
            color="secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </Container>
    </div>
  );
}
